'use server'

import { apiRequest, ApiResponse } from '../request'

export type FluidEntryType = 'INPUT' | 'OUTPUT'

export type FluidInputRoute =
    | 'ORAL'
    | 'IV'
    | 'ENTERAL'
    | 'PARENTERAL'
    | 'SUBCUTANEOUS'
    | 'OTHER'

export type FluidOutputRoute =
    | 'URINE'
    | 'STOOL'
    | 'VOMIT'
    | 'DRAIN'
    | 'ASPIRATION'
    | 'BLOOD'
    | 'SWEAT'
    | 'OTHER'

export interface FluidBalanceEntry {
    id: number
    type: FluidEntryType
    route: FluidInputRoute | FluidOutputRoute
    volume: number
    description?: string
    recordedAt: string
    recordedById?: number
    recordedBy?: {
        id: number
        name: string
    }
}

export interface FluidBalance {
    id: number
    companyId: number
    hospitalId: number
    admissionId: number
    admission?: {
        id: number
        client: {
            id: number
            name: string
        }
        bed?: {
            id: number
            number: string
        }
        unit?: {
            id: number
            name: string
        }
    }
    date: string
    shiftStart: string
    shiftEnd: string
    totalInput: number
    totalOutput: number
    balance: number
    closedAt?: string
    closedById?: number
    closedBy?: {
        id: number
        name: string
    }
    notes?: string
    entries?: FluidBalanceEntry[]
    createdAt: string
    updatedAt: string
}

export interface CreateFluidBalanceData {
    admissionId: number
    date?: string
    shiftStart?: string
    shiftEnd?: string
    notes?: string
}

export interface AddFluidEntryData {
    type: FluidEntryType
    route: FluidInputRoute | FluidOutputRoute
    volume: number
    description?: string
    recordedAt?: string
}

export interface FluidBalanceFilters {
    hospitalId?: number
    admissionId?: number
    startDate?: string
    endDate?: string
}

export async function getFluidBalances(
    companyId?: number,
    filters?: FluidBalanceFilters
): Promise<ApiResponse<FluidBalance[]>> {
    return apiRequest<FluidBalance[]>('hospital/fluid-balance', {
        params: { companyId, ...filters },
    })
}

export async function getFluidBalance(
    id: number
): Promise<ApiResponse<FluidBalance>> {
    return apiRequest<FluidBalance>(`hospital/fluid-balance/${id}`, {})
}

export async function getAdmissionFluidBalances(
    admissionId: number
): Promise<ApiResponse<FluidBalance[]>> {
    return apiRequest<FluidBalance[]>(
        `hospital/fluid-balance/admission/${admissionId}`,
        {}
    )
}

export async function getTodayFluidBalance(
    admissionId: number
): Promise<ApiResponse<FluidBalance | null>> {
    return apiRequest<FluidBalance | null>(
        `hospital/fluid-balance/today/${admissionId}`,
        {}
    )
}

export async function createFluidBalance(
    data: CreateFluidBalanceData
): Promise<ApiResponse<FluidBalance>> {
    return apiRequest<FluidBalance>('hospital/fluid-balance', { data }, 'post')
}

export async function addFluidEntry(
    balanceId: number,
    data: AddFluidEntryData
): Promise<ApiResponse<FluidBalance>> {
    return apiRequest<FluidBalance>(
        `hospital/fluid-balance/${balanceId}/entry`,
        { data },
        'post'
    )
}

export async function removeFluidEntry(
    balanceId: number,
    entryId: number
): Promise<ApiResponse<FluidBalance>> {
    return apiRequest<FluidBalance>(
        `hospital/fluid-balance/${balanceId}/entry/${entryId}`,
        {},
        'delete'
    )
}

export async function closeFluidBalance(
    id: number,
    notes?: string
): Promise<ApiResponse<FluidBalance>> {
    return apiRequest<FluidBalance>(
        `hospital/fluid-balance/${id}/close`,
        { data: { notes } },
        'patch'
    )
}

export async function deleteFluidBalance(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`hospital/fluid-balance/${id}`, {}, 'delete')
}

export const FLUID_ENTRY_TYPE_LABELS: Record<FluidEntryType, string> = {
    INPUT: 'Entrada',
    OUTPUT: 'Saída',
}

export const FLUID_INPUT_ROUTE_LABELS: Record<FluidInputRoute, string> = {
    ORAL: 'Via Oral',
    IV: 'Intravenoso',
    ENTERAL: 'Enteral',
    PARENTERAL: 'Parenteral',
    SUBCUTANEOUS: 'Subcutâneo',
    OTHER: 'Outro',
}

export const FLUID_OUTPUT_ROUTE_LABELS: Record<FluidOutputRoute, string> = {
    URINE: 'Diurese',
    STOOL: 'Evacuação',
    VOMIT: 'Vômito',
    DRAIN: 'Dreno',
    ASPIRATION: 'Aspiração',
    BLOOD: 'Sangue',
    SWEAT: 'Sudorese',
    OTHER: 'Outro',
}
